import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sun, Moon, Menu, User, LogOut, Cloud, CloudOff } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { Logo } from "../Logo";

function initials(firstName, lastName) {
  const a = (firstName || "").trim()[0] || "";
  const b = (lastName || "").trim()[0] || "";
  return (a + b).toUpperCase();
}

export default function TopBar({
  user,
  notificationsCount,
  darkMode,
  toggleDarkMode,
  onUploadLogo,
  isSynced,
}) {
  const navigate = useNavigate();
  const { user: authUser, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Chiudi menu cliccando fuori
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  // Nome da mostrare: preferisci il nome Google, fallback al profilo app
  const displayName = authUser?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Utente';
  const photoURL = authUser?.photoURL;

  return (
    <header className={`sticky top-0 z-10 backdrop-blur border-b transition-colors ${darkMode ? 'bg-neutral-900/80 border-neutral-800' : 'bg-white/80 border-neutral-200'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Left - Brand + Avatar */}
        <div className="flex items-center gap-4">
          {/* Logo Forfy - solo mobile (su desktop è nella Sidebar) */}
          <Logo
            variant="full"
            size="sm"
            darkMode={darkMode}
            className="cursor-pointer md:hidden"
          />

          {/* Avatar */}
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-full text-white flex items-center justify-center text-sm font-semibold overflow-hidden ${darkMode ? 'bg-neutral-700' : 'bg-neutral-900'}`}>
              {photoURL ? (
                <img
                  src={photoURL}
                  alt="Foto profilo"
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : user?.logoUrl ? (
                <img
                  src={user.logoUrl}
                  alt="Logo"
                  className="h-full w-full object-cover"
                />
              ) : (
                initials(user?.firstName, user?.lastName)
              )}
            </div>

            {/* Nome utente - nascosto su mobile per fare spazio */}
            <div className="leading-tight hidden lg:block">
              <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                {displayName}
              </div>
              {authUser?.email && (
                <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  {authUser.email}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right - Actions */}
        <div className="flex items-center gap-2 relative" ref={menuRef}>
          {/* Indicatore sync */}
          {isSynced !== undefined && (
            <div className={`p-2 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`} title={isSynced ? 'Sincronizzato' : 'Non sincronizzato'}>
              {isSynced ? (
                <Cloud className="h-4 w-4 text-emerald-500" />
              ) : (
                <CloudOff className="h-4 w-4 text-amber-500" />
              )}
            </div>
          )}

          {/* Notifiche */}
          <button className={`relative p-2 rounded-lg ${darkMode ? 'hover:bg-neutral-800' : 'hover:bg-neutral-100'}`}>
            <Bell className={`h-5 w-5 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`} />
            {notificationsCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                {notificationsCount}
              </span>
            )}
          </button>

          {/* Tema */}
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-yellow-400 hover:bg-neutral-800' : 'text-neutral-600 hover:bg-neutral-100'}`}
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {/* Menu utente (hamburger) */}
          <button
            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-neutral-800' : 'hover:bg-neutral-100'}`}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Menu className={`h-5 w-5 ${darkMode ? 'text-neutral-400' : 'text-neutral-700'}`} />
          </button>

          {/* Dropdown menu utente */}
          {menuOpen && (
            <div className={`absolute right-0 top-12 w-56 rounded-xl border shadow-lg py-2 z-20 ${darkMode ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-neutral-200'}`}>
              {/* Info utente */}
              <div className={`px-4 py-2 border-b ${darkMode ? 'border-neutral-800' : 'border-neutral-100'}`}>
                <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  {displayName}
                </div>
                {authUser?.email && (
                  <div className={`text-xs truncate ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    {authUser.email}
                  </div>
                )}
              </div>

              {/* Voci menu */}
              <button
                onClick={() => {
                  navigate("/settings");
                  setMenuOpen(false);
                }}
                className={`w-full px-4 py-2 text-sm flex items-center gap-2 ${darkMode ? 'text-neutral-300 hover:bg-neutral-800' : 'text-neutral-700 hover:bg-neutral-50'}`}
              >
                <User className="h-4 w-4" />
                Profilo e impostazioni
              </button>

              {/* Divider */}
              <div className={`my-1 border-t ${darkMode ? 'border-neutral-800' : 'border-neutral-100'}`} />

              {/* Logout */}
              <button
                onClick={handleSignOut}
                className={`w-full px-4 py-2 text-sm flex items-center gap-2 ${darkMode ? 'text-red-400 hover:bg-neutral-800' : 'text-red-600 hover:bg-red-50'}`}
              >
                <LogOut className="h-4 w-4" />
                Esci
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
